import React, { useState, useEffect, useCallback } from 'react';
import { cldThumb } from '../utils/img';
import { browseInventory, getItemTags, getItemBrands } from '../api/inventory';
import { useNavigation } from '../context/NavigationContext';
import { useSizes, useCategories } from '../api/meta';
import Modal from '../components/Modal';

const PRICE_TIERS = ['OPP', 'Mid', 'Premium', 'Unclassified'];

const STATUS_GROUPS = [
  { label: "Active Storage",  statuses: ["in_bin","in_mini_pack"] },
  { label: "In Progress",     statuses: ["inbounded","reversed","in_ironing_queue","in_repair_queue","in_retag_queue","picked_from_customer"] },
  { label: "Dispatched",      statuses: ["in_transit","in_full_pack"] },
  { label: "Inactive",        statuses: ["pre_inbound","sold","damaged","missing","returned_to_brand","liquidated","in_search_queue","gifted","non_revenue"] },
];

const DEFAULT_CHECKED = new Set([
  "in_bin","in_mini_pack","inbounded","reversed",
  "in_ironing_queue","in_repair_queue","in_retag_queue","picked_from_customer",
  "in_transit","in_full_pack",
]);

const STATUS_COLOURS = {
  in_bin:"bg-green-100 text-green-700", in_mini_pack:"bg-green-100 text-green-700",
  in_full_pack:"bg-blue-100 text-blue-700", in_transit:"bg-blue-100 text-blue-700",
  inbounded:"bg-yellow-100 text-yellow-700", pre_inbound:"bg-yellow-50 text-yellow-500",
  in_ironing_queue:"bg-orange-100 text-orange-700", in_repair_queue:"bg-orange-100 text-orange-600",
  in_retag_queue:"bg-orange-100 text-orange-700",
  reversed:"bg-purple-100 text-purple-700", sold:"bg-gray-100 text-gray-400",
  damaged:"bg-red-100 text-red-600", missing:"bg-red-100 text-red-600",
  returned_to_brand:"bg-gray-100 text-gray-400", picked_from_customer:"bg-purple-100 text-purple-600",
  liquidated:"bg-gray-50 text-gray-400", in_search_queue:"bg-red-50 text-red-500",
  gifted:"bg-gray-100 text-gray-400", non_revenue:"bg-gray-100 text-gray-400",
};

const ALL_COLS = [
  { key:"zoddle_barcode",    label:"Barcode",        default:true  },
  { key:"design_name",       label:"Design",         default:true  },
  { key:"brand_name",        label:"Brand",          default:true  },
  { key:"gender",            label:"Gender",         default:true  },
  { key:"category",          label:"Category",       default:true  },
  { key:"size",              label:"Size",           default:true  },
  { key:"use_case_tags",     label:"Use Case Tags",  default:true  },
  { key:"status",            label:"Status",         default:true  },
  { key:"condition",         label:"Condition",      default:true  },
  { key:"location_type",     label:"Location Type",  default:true  },
  { key:"location_label",    label:"Location",       default:true  },
  { key:"mrp",               label:"MRP",            default:true  },
  { key:"zoddle_price",      label:"Zoddle Price",   default:true  },
  { key:"available_to_pack", label:"Avail. to Pack", default:true  },
  { key:"images",            label:"Images",         default:true  },
  { key:"item_id",           label:"Item UUID",      default:false },
  { key:"design_code",       label:"Design Code",    default:false },
  { key:"color",             label:"Color",          default:false },
  { key:"style_tags",        label:"Style Tags",     default:false },
  { key:"location_id",       label:"Location UUID",  default:false },
  { key:"saver_price",       label:"Saver Price",    default:false },
  { key:"inventorized",      label:"Inventorized",   default:false },
  { key:"ownership_type",    label:"Ownership",      default:false },
  { key:"last_updated",      label:"Last Updated",   default:false },
];

// ── CSV export helpers ──
function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}
function csvCell(col, item) {
  if (col.key === "available_to_pack" || col.key === "inventorized") return item[col.key] ? "Yes" : "No";
  return item[col.key];
}

// Actual selling price = lower of Zoddle Price and Saver Price (matches the
// effective-price convention used elsewhere, e.g. Order Ops / Customer Catalog).
function effectivePrice(item) {
  const zp = item?.zoddle_price;
  const sp = item?.saver_price;
  if (sp == null) return zp;
  return Math.min(zp, sp);
}

// Card tint by brand price tier — same mapping as the Order Ops packing grid.
function tierCardClass(tier) {
  if (tier === 'Premium') return 'bg-red-50 border-red-100';
  if (tier === 'OPP') return 'bg-green-50 border-green-100';
  return 'bg-white border-gray-100';
}

function GridCard({ item, onClick }) {
  const eff = effectivePrice(item);
  const showMrp = item.mrp && eff && item.mrp > eff * 1.1;
  return (
    <div onClick={onClick}
      className={`border rounded-xl p-2 cursor-pointer hover:shadow-md hover:border-zoddle-green transition ${tierCardClass(item.price_tier)}`}>
      {item.image_url_1
        ? <img src={cldThumb(item.image_url_1, 300)} alt={item.design_name || ''} loading="lazy"
            className="w-full aspect-square object-cover rounded-lg bg-gray-100 mb-2" />
        : <div className="w-full aspect-square rounded-lg bg-gray-100 mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
      <p className="text-xs font-mono font-bold text-zoddle-blue truncate">{item.zoddle_barcode}</p>
      <p className="text-xs text-gray-500 truncate">{item.brand_name || '—'}</p>
      <p className="text-xs text-gray-400">{[item.size, item.gender].filter(Boolean).join(' · ') || '—'}</p>
      <div className="flex items-center justify-between mt-1">
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-bold text-zoddle-green">₹{eff}</span>
          {showMrp && <span className="text-xs text-gray-300 line-through">₹{item.mrp}</span>}
        </div>
      </div>
      <div className="mt-1"><StatusBadge value={item.status} /></div>
    </div>
  );
}

function StatusBadge({ value }) {
  const cls = STATUS_COLOURS[value] || "bg-gray-100 text-gray-500";
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{value?.replace(/_/g," ")}</span>;
}

function Tags({ value }) {
  if (!value) return <span className="text-gray-300 text-xs">—</span>;
  const tags = value.split(";").map(t => t.trim()).filter(Boolean);
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(t => <span key={t} className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{t}</span>)}
    </div>
  );
}

function ImagesCell({ item, onClick }) {
  const imgs = [item.image_url_1,item.image_url_2,item.image_url_3,item.image_url_4].filter(Boolean);
  if (!imgs.length) return <span className="text-gray-300 text-xs">—</span>;
  return (
    <button onClick={e => { e.stopPropagation(); onClick(imgs); }}
      className="flex items-center gap-1 text-zoddle-blue hover:underline text-xs font-medium">
      🖼 {imgs.length}
    </button>
  );
}

function ImageModal({ images, onClose }) {
  return (
    <Modal onClose={onClose}>
      <div className="p-4 w-full max-w-lg">
        <h3 className="text-sm font-bold text-gray-700 mb-3">Images</h3>
        <div className={`grid gap-3 ${images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {images.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer">
              <img src={cldThumb(url, 600)} alt={`img-${i}`}
                className="w-full h-48 object-cover rounded-lg border border-gray-100 hover:opacity-90 transition" />
            </a>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function ColCell({ col, item }) {
  const v = item[col];
  if (col === "status")            return <StatusBadge value={v} />;
  if (col === "use_case_tags" || col === "style_tags") return <Tags value={v} />;
  if (col === "available_to_pack" || col === "inventorized")
    return <span className={`text-xs font-semibold ${v ? "text-green-600":"text-gray-400"}`}>{v?"Yes":"No"}</span>;
  if (col === "mrp" || col === "zoddle_price" || col === "saver_price")
    return <span className="text-sm font-medium text-gray-700">₹{v}</span>;
  if (col === "condition") {
    const cls = {good:"text-green-600",needs_ironing:"text-orange-500",needs_repair:"text-orange-600",damaged:"text-red-600"}[v]||"text-gray-500";
    return <span className={`text-xs font-medium ${cls}`}>{v?.replace(/_/g," ")}</span>;
  }
  if (col === "last_updated") return <span className="text-xs text-gray-400">{v?.slice(0,16).replace("T"," ")}</span>;
  if (col === "item_id" || col === "location_id")
    return <span className="text-xs font-mono text-gray-400 block truncate max-w-[120px]">{v || "—"}</span>;
  if (col === "zoddle_barcode") return <span className="text-xs font-mono font-bold text-zoddle-blue underline">{v}</span>;
  if (col === "location_label") return <span className="text-xs font-mono text-gray-600 font-medium">{v || "—"}</span>;
  if (!v) return <span className="text-gray-300 text-xs">—</span>;
  return <span className="text-sm text-gray-700 block truncate max-w-[160px]">{v}</span>;
}

export default function InventoryBrowser() {
  const { navigate } = useNavigation();
  const sizes = useSizes();
  const categories = useCategories();

  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [tags, setTags]         = useState({ use_case_tags:[], style_tags:[] });
  const [brands, setBrands]     = useState([]);
  const [imageModal, setImageModal]     = useState(null);
  const [showColPicker, setShowColPicker] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("zocs_inventory_view_mode") || "list"; } catch { return "list"; }
  });
  const [loadingMore, setLoadingMore] = useState(false);

  function switchView(mode) {
    setViewMode(mode);
    try { localStorage.setItem("zocs_inventory_view_mode", mode); } catch {}
  }

  const [checkedStatuses, setCheckedStatuses] = useState(new Set(DEFAULT_CHECKED));
  const [dropdowns, setDropdowns] = useState({
    gender:"", size:"", brand_name:"", category:"", price_tier:"",
    ownership_type:"", condition:"", use_case_tag:"", style_tag:"",
  });
  const [search, setSearch]         = useState("");
  const [pendingSearch, setPending] = useState("");
  const [priceMin, setPriceMin]     = useState("");
  const [priceMax, setPriceMax]     = useState("");
  const [pendingMin, setPendingMin] = useState("");
  const [pendingMax, setPendingMax] = useState("");

  const [visibleCols, setVisibleCols] = useState(new Set(ALL_COLS.filter(c=>c.default).map(c=>c.key)));
  const [sortCol, setSortCol]   = useState(null);
  const [sortDir, setSortDir]   = useState("asc");

  useEffect(() => {
    getItemTags().then(setTags).catch(()=>{});
    getItemBrands().then(d => setBrands(d.brands || [])).catch(()=>{});
  }, []);

  const load = useCallback(async (overrides = {}) => {
    setLoading(true); setError(null);
    try {
      const f = {
        statuses: [...(overrides.checkedStatuses ?? checkedStatuses)],
        ...dropdowns, ...(overrides.dropdowns || {}),
        search:    overrides.search    ?? search,
        price_min: overrides.price_min ?? (priceMin !== "" ? parseFloat(priceMin) : null),
        price_max: overrides.price_max ?? (priceMax !== "" ? parseFloat(priceMax) : null),
      };
      const data = await browseInventory(f);
      setItems(data.items); setTotal(data.total);
    } catch { setError("Failed to load inventory."); }
    finally { setLoading(false); }
  }, [checkedStatuses, dropdowns, search, priceMin, priceMax]);

  useEffect(() => { load(); }, [checkedStatuses, dropdowns]);

  // Grid-view "Load more" — fetches the next page and appends (list view stays capped at 300).
  async function loadMore() {
    setLoadingMore(true);
    try {
      const f = {
        statuses: [...checkedStatuses], ...dropdowns,
        search,
        price_min: priceMin !== "" ? parseFloat(priceMin) : null,
        price_max: priceMax !== "" ? parseFloat(priceMax) : null,
        offset: items.length,
      };
      const data = await browseInventory(f);
      setItems(prev => [...prev, ...data.items]); setTotal(data.total);
    } catch { setError("Failed to load more."); }
    finally { setLoadingMore(false); }
  }

  // Group toggle — check all if any unchecked, uncheck all if all checked
  function toggleGroup(group) {
    const allChecked = group.statuses.every(s => checkedStatuses.has(s));
    setCheckedStatuses(prev => {
      const next = new Set(prev);
      group.statuses.forEach(s => allChecked ? next.delete(s) : next.add(s));
      return next;
    });
  }

  function toggleStatus(s) {
    setCheckedStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }

  function setDD(k, v) { setDropdowns(d => ({ ...d, [k]: v })); }

  function handleSearchKey(e) {
    if (e.key === "Enter") { setSearch(pendingSearch); load({ search: pendingSearch }); }
  }

  function handlePriceKey(e) {
    if (e.key === "Enter") {
      const mn = pendingMin !== "" ? parseFloat(pendingMin) : null;
      const mx = pendingMax !== "" ? parseFloat(pendingMax) : null;
      setPriceMin(pendingMin); setPriceMax(pendingMax);
      load({ price_min: mn, price_max: mx });
    }
  }

  function clearFilters() {
    setDropdowns({ gender:"",size:"",brand_name:"",category:"",price_tier:"",ownership_type:"",condition:"",use_case_tag:"",style_tag:"" });
    setSearch(""); setPending("");
    setPriceMin(""); setPriceMax(""); setPendingMin(""); setPendingMax("");
    load({ dropdowns:{ gender:"",size:"",brand_name:"",category:"",price_tier:"",ownership_type:"",condition:"",use_case_tag:"",style_tag:"" }, search:"", price_min:null, price_max:null });
  }

  function toggleCol(k) {
    setVisibleCols(prev => { const n = new Set(prev); n.has(k)?n.delete(k):n.add(k); return n; });
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d==="asc"?"desc":"asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  function handleRowClick(item) {
    navigate('itemhistory', { query: item.zoddle_barcode });
  }

  const sortedItems = sortCol ? [...items].sort((a,b) => {
    const av = a[sortCol] ?? ""; const bv = b[sortCol] ?? "";
    return sortDir==="asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  }) : items;
  const listItems = sortedItems.slice(0, 300); // list view stays capped at 300, same as before grid existed

  const orderedVisible = ALL_COLS.filter(c => visibleCols.has(c.key) && c.key !== "images")
    .concat(ALL_COLS.filter(c => c.key === "images" && visibleCols.has("images")));

  // Export ALL items matching the current filter (not just the 300 shown) as CSV,
  // using the currently-visible columns.
  async function handleExportCsv() {
    setExporting(true); setError(null);
    try {
      const f = {
        statuses: [...checkedStatuses], ...dropdowns,
        search,
        price_min: priceMin !== "" ? parseFloat(priceMin) : null,
        price_max: priceMax !== "" ? parseFloat(priceMax) : null,
        limit: 1000000,
      };
      const data = await browseInventory(f);
      const rows = data.items || [];
      const cols = orderedVisible.filter(c => c.key !== "images");
      const imageCols = ["Image URL 1", "Image URL 2", "Image URL 3", "Image URL 4"];
      const header = [...cols.map(c => c.label), ...imageCols].map(csvEscape).join(",");
      const body = rows.map(it => [...cols.map(c => csvCell(c, it)), it.image_url_1, it.image_url_2, it.image_url_3, it.image_url_4].map(csvEscape).join(",")).join("\n");
      const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventory_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setError("Export failed."); }
    finally { setExporting(false); }
  }

  const hasActiveFilter = Object.values(dropdowns).some(v=>v) || search || priceMin || priceMax;

  return (
    <div className="flex h-[calc(100vh-65px)]">
      {/* Sidebar */}
      <div className="w-52 shrink-0 bg-white border-r border-gray-100 p-4 overflow-y-auto">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Show items</p>
        {STATUS_GROUPS.map(g => {
          const allChecked = g.statuses.every(s => checkedStatuses.has(s));
          const someChecked = g.statuses.some(s => checkedStatuses.has(s));
          return (
            <div key={g.label} className="mb-4">
              <button onClick={() => toggleGroup(g)}
                className="flex items-center gap-1.5 mb-1.5 group w-full text-left">
                <span className={`w-3 h-3 rounded border flex items-center justify-center text-white text-xs shrink-0
                  ${allChecked ? "bg-zoddle-green border-zoddle-green" : someChecked ? "bg-zoddle-green/40 border-zoddle-green/40" : "border-gray-300 bg-white"}`}>
                  {allChecked && "✓"}
                </span>
                <span className="text-xs font-semibold text-gray-600 group-hover:text-gray-800">{g.label}</span>
              </button>
              {g.statuses.map(s => (
                <label key={s} className="flex items-center gap-2 cursor-pointer py-0.5 pl-4 group">
                  <input type="checkbox" checked={checkedStatuses.has(s)}
                    onChange={() => toggleStatus(s)} className="accent-zoddle-green" />
                  <span className="text-xs text-gray-500 group-hover:text-gray-700 leading-tight">
                    {s.replace(/_/g," ")}
                  </span>
                </label>
              ))}
            </div>
          );
        })}
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Filter bar */}
        <div className="bg-white border-b border-gray-100 px-5 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <input type="text" value={pendingSearch}
              onChange={e => setPending(e.target.value)} onKeyDown={handleSearchKey}
              placeholder="Search barcode / design (Enter)…"
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-zoddle-green w-52" />

            {/* Standard dropdowns */}
            {[
              ["Gender","gender",[["girl","Girl"],["boy","Boy"],["unisex","Unisex"]]],
              ["Size","size",sizes.map(s=>[s,s])],
              ["Condition","condition",[["good","Good"],["needs_ironing","Needs ironing"],["needs_repair","Needs repair"],["damaged","Damaged"]]],
              ["Ownership","ownership_type",[["standard","Standard"],["sor","SOR"]]],
            ].map(([label,key,opts]) => (
              <select key={key} value={dropdowns[key]} onChange={e => setDD(key, e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
                <option value="">{label}</option>
                {opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ))}

            {/* Brand */}
            <select value={dropdowns.brand_name} onChange={e => setDD("brand_name", e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">Brand</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>

            {/* Category */}
            <select value={dropdowns.category} onChange={e => setDD("category", e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">Category</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            {/* Price tier */}
            <select value={dropdowns.price_tier} onChange={e => setDD("price_tier", e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">Price tier</option>
              {PRICE_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            {/* Use case tags */}
            <select value={dropdowns.use_case_tag} onChange={e => setDD("use_case_tag", e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">Use case tag</option>
              {tags.use_case_tags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            {/* Style tags */}
            <select value={dropdowns.style_tag} onChange={e => setDD("style_tag", e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">Style tag</option>
              {tags.style_tags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            {/* Price range */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">₹</span>
              <input type="number" value={pendingMin} onChange={e => setPendingMin(e.target.value)}
                onKeyDown={handlePriceKey} placeholder="Min"
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-zoddle-green w-20" />
              <span className="text-xs text-gray-400">–</span>
              <input type="number" value={pendingMax} onChange={e => setPendingMax(e.target.value)}
                onKeyDown={handlePriceKey} placeholder="Max"
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-zoddle-green w-20" />
            </div>

            {hasActiveFilter && (
              <button onClick={clearFilters}
                className="text-xs text-gray-400 hover:text-zoddle-pink transition-colors">
                Clear
              </button>
            )}
          </div>

          {/* Count + Columns — own row, Columns always right-aligned */}
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-400">
              {loading ? "Loading…" : `${total.toLocaleString()} item${total!==1?"s":""}`}
            </p>

            {/* View toggle + Export + Column picker */}
            <div className="flex items-center gap-2">
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button onClick={() => switchView("list")}
                className={`text-xs px-3 py-1.5 font-medium transition-colors ${viewMode==="list" ? "bg-zoddle-green text-white" : "text-gray-600 hover:bg-gray-50"}`}>
                List
              </button>
              <button onClick={() => switchView("grid")}
                className={`text-xs px-3 py-1.5 font-medium transition-colors border-l border-gray-200 ${viewMode==="grid" ? "bg-zoddle-green text-white" : "text-gray-600 hover:bg-gray-50"}`}>
                Grid
              </button>
            </div>
            <button onClick={handleExportCsv} disabled={exporting || loading}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 hover:border-zoddle-green transition-colors font-medium disabled:opacity-50">
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
            {viewMode === "list" && (
            <div className="relative">
              <button onClick={() => setShowColPicker(v => !v)}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 hover:border-zoddle-blue transition-colors font-medium">
                Columns ▾
              </button>
              {showColPicker && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowColPicker(false)} />
                  <div className="absolute right-0 top-9 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56 max-h-80 overflow-y-auto">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Columns</p>
                    {ALL_COLS.filter(c => c.key !== "images").map(c => (
                      <label key={c.key} className="flex items-center gap-2 py-0.5 cursor-pointer">
                        <input type="checkbox" checked={visibleCols.has(c.key)}
                          onChange={() => toggleCol(c.key)} className="accent-zoddle-green" />
                        <span className="text-xs text-gray-700">{c.label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            )}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-auto">
          {error && (
            <div className="m-4 bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-zoddle-pink">{error}</div>
          )}
          {!loading && items.length === 0 && !error && (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400 text-sm">No items match the current filters.</p>
            </div>
          )}
          {viewMode === "list" && (items.length > 0 || loading) && (
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  {orderedVisible.map(c => (
                    <th key={c.key}
                      onClick={c.key !== "images" ? () => handleSort(c.key) : undefined}
                      className={`px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap border-b border-gray-100 ${c.key!=="images"?"cursor-pointer hover:text-gray-600 select-none":""}`}>
                      {c.label}{sortCol===c.key && <span className="ml-1">{sortDir==="asc"?"↑":"↓"}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={orderedVisible.length} className="text-center py-12 text-gray-400 text-sm">Loading…</td></tr>
                ) : listItems.map((item, i) => (
                  <tr key={item.item_id}
                    className={`border-b border-gray-50 hover:bg-zoddle-green/5 transition-colors ${i%2===0?"":"bg-gray-50/30"}`}>
                    {orderedVisible.map(c => (
                      <td key={c.key}
                        onClick={c.key === "zoddle_barcode" ? () => handleRowClick(item) : undefined}
                        className={`px-4 py-2.5 max-w-[200px] ${c.key === "zoddle_barcode" ? "cursor-pointer" : ""}`}>
                        {c.key === "images"
                          ? <ImagesCell item={item} onClick={setImageModal} />
                          : <ColCell col={c.key} item={item} />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {viewMode === "list" && total > 300 && (
            <p className="text-center text-xs text-gray-400 py-3">
              Showing 300 of {total} — apply filters to narrow down.
            </p>
          )}

          {viewMode === "grid" && !loading && items.length > 0 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 p-4">
                {sortedItems.map(item => (
                  <GridCard key={item.item_id} item={item} onClick={() => handleRowClick(item)} />
                ))}
              </div>
              <div className="text-center pb-6">
                <p className="text-xs text-gray-400 mb-2">Showing {items.length.toLocaleString()} of {total.toLocaleString()}</p>
                {items.length < total && (
                  <button onClick={loadMore} disabled={loadingMore}
                    className="text-xs border border-gray-200 rounded-lg px-4 py-1.5 text-gray-600 hover:border-zoddle-green transition-colors font-medium disabled:opacity-50">
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                )}
              </div>
            </>
          )}
          {viewMode === "grid" && loading && (
            <p className="text-center text-sm text-gray-400 py-12">Loading…</p>
          )}
        </div>
      </div>

      {imageModal && <ImageModal images={imageModal} onClose={() => setImageModal(null)} />}
    </div>
  );
}
